import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Security,
  Notifications,
  Payment,
  Email,
  Sms,
  Save,
  Refresh,
  Edit,
} from '@mui/icons-material';
import { settingsService } from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Settings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  const [settings, setSettings] = useState({
    general: {
      siteName: 'Plug Vault Plus',
      siteDescription: 'Premium membership platform',
      timezone: 'Africa/Johannesburg',
      currency: 'ZAR',
      language: 'en',
      maintenanceMode: false,
    },
    security: {
      passwordComplexity: true,
      twoFactorAuth: false,
      sessionTimeout: 30,
      maxLoginAttempts: 5,
      requireEmailVerification: true,
    },
    notifications: {
      emailNotifications: true,
      smsNotifications: true,
      pushNotifications: false,
      marketingEmails: false,
      systemAlerts: true,
    },
    payments: {
      stripeEnabled: true,
      paypalEnabled: false,
      bankTransferEnabled: true,
      minimumPayment: 50,
      processingFee: 2.5,
      currency: 'ZAR',
    },
    email: {
      smtpHost: 'smtp.gmail.com',
      smtpPort: 587,
      smtpUsername: '',
      smtpPassword: '',
      fromEmail: 'noreply@plugvaultplus.com',
      fromName: 'Plug Vault Plus',
    },
    sms: {
      provider: 'twilio',
      apiKey: '',
      apiSecret: '',
      fromNumber: '',
      enabled: false,
    },
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await settingsService.getAll();
      setSettings({ ...settings, ...data });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (category: string) => {
    try {
      setSaving(true);
      setError(null);
      
      const categorySettings = (settings as any)[category];
      for (const [key, value] of Object.entries(categorySettings)) {
        await settingsService.update(`${category}.${key}`, value);
      }
      
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (category: string, key: string, value: any) => {
    setSettings({
      ...settings,
      [category]: {
        ...(settings as any)[category],
        [key]: value,
      },
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#FFD700' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
          Settings
        </Typography>
        <Typography variant="body1" sx={{ color: '#888' }}>
          Configure platform settings and preferences
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            textColor="inherit"
            indicatorColor="primary"
            sx={{
              '& .MuiTab-root': {
                color: '#888',
                '&.Mui-selected': {
                  color: '#FFD700',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#FFD700',
              },
            }}
          >
            <Tab icon={<SettingsIcon />} label="General" />
            <Tab icon={<Security />} label="Security" />
            <Tab icon={<Notifications />} label="Notifications" />
            <Tab icon={<Payment />} label="Payments" />
            <Tab icon={<Email />} label="Email" />
            <Tab icon={<Sms />} label="SMS" />
          </Tabs>
        </Box>

        {/* General Settings */}
        <TabPanel value={tabValue} index={0}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Site Name"
                  value={settings.general.siteName}
                  onChange={(e) => handleSettingChange('general', 'siteName', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#888' }}>Timezone</InputLabel>
                  <Select
                    value={settings.general.timezone}
                    onChange={(e) => handleSettingChange('general', 'timezone', e.target.value)}
                    label="Timezone"
                    sx={{
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    }}
                  >
                    <MenuItem value="Africa/Johannesburg">Africa/Johannesburg</MenuItem>
                    <MenuItem value="UTC">UTC</MenuItem>
                    <MenuItem value="America/New_York">America/New_York</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Site Description"
                  value={settings.general.siteDescription}
                  onChange={(e) => handleSettingChange('general', 'siteDescription', e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#888' }}>Currency</InputLabel>
                  <Select
                    value={settings.general.currency}
                    onChange={(e) => handleSettingChange('general', 'currency', e.target.value)}
                    label="Currency"
                    sx={{
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    }}
                  >
                    <MenuItem value="ZAR">South African Rand (ZAR)</MenuItem>
                    <MenuItem value="USD">US Dollar (USD)</MenuItem>
                    <MenuItem value="EUR">Euro (EUR)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.general.maintenanceMode}
                      onChange={(e) => handleSettingChange('general', 'maintenanceMode', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }}
                    />
                  }
                  label="Maintenance Mode"
                  sx={{ color: 'white' }}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                onClick={() => handleSave('general')}
                disabled={saving}
                sx={{
                  bgcolor: '#FFD700',
                  color: '#0A0A0A',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: '#E6C200' },
                }}
              >
                Save General Settings
              </Button>
            </Box>
          </CardContent>
        </TabPanel>

        {/* Security Settings */}
        <TabPanel value={tabValue} index={1}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.security.passwordComplexity}
                      onChange={(e) => handleSettingChange('security', 'passwordComplexity', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }}
                    />
                  }
                  label="Enforce Password Complexity"
                  sx={{ color: 'white' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.security.twoFactorAuth}
                      onChange={(e) => handleSettingChange('security', 'twoFactorAuth', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }}
                    />
                  }
                  label="Enable Two-Factor Authentication"
                  sx={{ color: 'white' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Session Timeout (minutes)"
                  type="number"
                  value={settings.security.sessionTimeout}
                  onChange={(e) => handleSettingChange('security', 'sessionTimeout', Number(e.target.value))}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Max Login Attempts"
                  type="number"
                  value={settings.security.maxLoginAttempts}
                  onChange={(e) => handleSettingChange('security', 'maxLoginAttempts', Number(e.target.value))}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.security.requireEmailVerification}
                      onChange={(e) => handleSettingChange('security', 'requireEmailVerification', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }}
                    />
                  }
                  label="Require Email Verification"
                  sx={{ color: 'white' }}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                onClick={() => handleSave('security')}
                disabled={saving}
                sx={{
                  bgcolor: '#FFD700',
                  color: '#0A0A0A',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: '#E6C200' },
                }}
              >
                Save Security Settings
              </Button>
            </Box>
          </CardContent>
        </TabPanel>

        {/* Notification Settings */}
        <TabPanel value={tabValue} index={2}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.emailNotifications}
                      onChange={(e) => handleSettingChange('notifications', 'emailNotifications', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }}
                    />
                  }
                  label="Email Notifications"
                  sx={{ color: 'white' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.smsNotifications}
                      onChange={(e) => handleSettingChange('notifications', 'smsNotifications', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }}
                    />
                  }
                  label="SMS Notifications"
                  sx={{ color: 'white' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.pushNotifications}
                      onChange={(e) => handleSettingChange('notifications', 'pushNotifications', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }}
                    />
                  }
                  label="Push Notifications"
                  sx={{ color: 'white' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.systemAlerts}
                      onChange={(e) => handleSettingChange('notifications', 'systemAlerts', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }}
                    />
                  }
                  label="System Alerts"
                  sx={{ color: 'white' }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.marketingEmails}
                      onChange={(e) => handleSettingChange('notifications', 'marketingEmails', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }}
                    />
                  }
                  label="Marketing Emails"
                  sx={{ color: 'white' }}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                onClick={() => handleSave('notifications')}
                disabled={saving}
                sx={{
                  bgcolor: '#FFD700',
                  color: '#0A0A0A',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: '#E6C200' },
                }}
              >
                Save Notification Settings
              </Button>
            </Box>
          </CardContent>
        </TabPanel>

        {/* Payment Settings */}
        <TabPanel value={tabValue} index={3}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.payments.stripeEnabled}
                      onChange={(e) => handleSettingChange('payments', 'stripeEnabled', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }}
                    />
                  }
                  label="Stripe Payments"
                  sx={{ color: 'white' }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.payments.paypalEnabled}
                      onChange={(e) => handleSettingChange('payments', 'paypalEnabled', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }}
                    />
                  }
                  label="PayPal Payments"
                  sx={{ color: 'white' }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.payments.bankTransferEnabled}
                      onChange={(e) => handleSettingChange('payments', 'bankTransferEnabled', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }}
                    />
                  }
                  label="Bank Transfer"
                  sx={{ color: 'white' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Minimum Payment Amount"
                  type="number"
                  value={settings.payments.minimumPayment}
                  onChange={(e) => handleSettingChange('payments', 'minimumPayment', Number(e.target.value))}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Processing Fee (%)"
                  type="number"
                  value={settings.payments.processingFee}
                  onChange={(e) => handleSettingChange('payments', 'processingFee', Number(e.target.value))}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                  }}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                onClick={() => handleSave('payments')}
                disabled={saving}
                sx={{
                  bgcolor: '#FFD700',
                  color: '#0A0A0A',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: '#E6C200' },
                }}
              >
                Save Payment Settings
              </Button>
            </Box>
          </CardContent>
        </TabPanel>

        {/* Email Settings */}
        <TabPanel value={tabValue} index={4}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="SMTP Host"
                  value={settings.email.smtpHost}
                  onChange={(e) => handleSettingChange('email', 'smtpHost', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="SMTP Port"
                  type="number"
                  value={settings.email.smtpPort}
                  onChange={(e) => handleSettingChange('email', 'smtpPort', Number(e.target.value))}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="From Email"
                  type="email"
                  value={settings.email.fromEmail}
                  onChange={(e) => handleSettingChange('email', 'fromEmail', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="From Name"
                  value={settings.email.fromName}
                  onChange={(e) => handleSettingChange('email', 'fromName', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                  }}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                onClick={() => handleSave('email')}
                disabled={saving}
                sx={{
                  bgcolor: '#FFD700',
                  color: '#0A0A0A',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: '#E6C200' },
                }}
              >
                Save Email Settings
              </Button>
            </Box>
          </CardContent>
        </TabPanel>

        {/* SMS Settings */}
        <TabPanel value={tabValue} index={5}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.sms.enabled}
                      onChange={(e) => handleSettingChange('sms', 'enabled', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }}
                    />
                  }
                  label="Enable SMS Service"
                  sx={{ color: 'white' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#888' }}>SMS Provider</InputLabel>
                  <Select
                    value={settings.sms.provider}
                    onChange={(e) => handleSettingChange('sms', 'provider', e.target.value)}
                    label="SMS Provider"
                    sx={{
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    }}
                  >
                    <MenuItem value="twilio">Twilio</MenuItem>
                    <MenuItem value="clickatell">Clickatell</MenuItem>
                    <MenuItem value="bulksms">BulkSMS</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="From Number"
                  value={settings.sms.fromNumber}
                  onChange={(e) => handleSettingChange('sms', 'fromNumber', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                  }}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                onClick={() => handleSave('sms')}
                disabled={saving}
                sx={{
                  bgcolor: '#FFD700',
                  color: '#0A0A0A',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: '#E6C200' },
                }}
              >
                Save SMS Settings
              </Button>
            </Box>
          </CardContent>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default Settings;